import React from 'react';
import { get, map, reduce } from 'lodash';
import { Tooltip, TooltipData } from '../../elements-tooltip/tooltip';
import { formatPrice } from '../utils';

export const useInquiryPriceBreakdown = (quote, listing, profile, intl) => {
  const booking = get(quote, 'children[0].meta.booking', {});
  const masterContract = get(quote, 'master', {});

  const currency = get(listing, 'prices[0].currency', '');
  const subtotalAmount = get(booking, 'subtotal_amount', 0);
  const rate = get(booking, 'rate', 0);
  const duration = get(booking, 'duration', 0);
  const discounts = get(booking, 'discounts', []);
  const { dayRateDiscount, otherDiscounts } = reduce(discounts, ((map, discount) => {
    const type = get(discount, 'meta.type', get(discount, 'type', ''));
    if ('DAY_RATE_DISCOUNT' === type) {
      return { ...map, dayRateDiscount: discount };
    }
    const otherDiscounts = get(map, 'otherDiscounts', []);
    otherDiscounts.push(otherDiscounts, discount);
    return { ...map, otherDiscounts };
  }), {});
  const dayRateDiscountAmount = get(dayRateDiscount, 'discount_amount', 0);
  const dayRateDiscountValue = get(dayRateDiscount, 'value', 0);
  const additionalCharges = get(booking, 'addtl_charges', []);
  const processingFee = get(masterContract, 'calculations.guest.fee', 0)
  || get(booking, 'guest_payment_processing_fee', 0);
  const total = get(masterContract, 'calculations.guest.payment', 0);

  // Subtotal
  const subtotalItem = {
    label: `${formatPrice(rate, currency, intl)} x ${duration} ${
      1 < duration
        ? intl.formatMessage({
          id: 'inquiry_booking.inquiry_price_breakdown.hours',
          defaultMessage: 'hours'
        })
        : intl.formatMessage({
          id: 'inquiry_booking.inquiry_price_breakdown.hour',
          defaultMessage: 'hour'
        })}`,
    price: formatPrice(subtotalAmount, currency, intl),
    tooltip:
      <Tooltip
        tooltip={TooltipData.VARIABLE_PRICING(
          listing,
          booking.rate_details
        )}
      />
  };

  // Day Rate Discount
  const dayRateDiscountItem = dayRateDiscount ? {
    label: intl.formatMessage({
      id: 'inquiry_booking.inquiry_price_breakdown.day_rate_discount_label',
      defaultMessage: '8+ hour discount ({percent})'
    }, { percent: intl.formatNumber(dayRateDiscountValue / 100, { style: 'percent' }) }),
    price: formatPrice(-dayRateDiscountAmount, currency, intl)
  } : null;

  // Additional Charges
  const additionalChargesItems = map(additionalCharges, (charge) => {
    const label = get(charge, 'charge_str', '');
    const price = get(charge, 'value', 0);
    const type = get(charge, 'type', '');
    const tooltipData = get(TooltipData, `[${type}]`, '');
    return {
      label,
      price: formatPrice(price, currency, intl),
      tooltip: tooltipData ? <Tooltip tooltip={tooltipData} /> : null
    };
  });

  // Other Discounts
  const otherDiscountsItems = map(otherDiscounts, (discount) => {
    const discountStr = get(discount, 'discount_str', '');
    const value = get(discount, 'value', 0);
    const maxAmount = get(discount, 'max_amount', 0);
    const applyAs = get(discount, 'apply_as', '');
    const title = get(discount, 'title', '');
    const price = get(discount, 'discount_amount', 0);
    const isPercentOff = 'PERCENT_OFF' === applyAs;

    const labelStr = `${intl.formatMessage({
      id: 'inquiry_booking.inquiry_price_breakdown.other_discount_label',
      defaultMessage: '{value}{percent} off'
    }, { value: isPercentOff ? value : formatPrice(value, currency, intl), percent: isPercentOff ? '%' : '' })}`;

    const maxAmountStr = maxAmount ? `${intl.formatMessage({
      id: 'inquiry_booking.inquiry_price_breakdown.other_discount_max_amount_label',
      defaultMessage: ', up to {amount}'
    }, { amount: formatPrice(maxAmount, currency, intl) })}` : '';

    return {
      label: discountStr || <>
        {title}
        <br/>
        {labelStr}
        {maxAmountStr}
      </>,
      price: formatPrice(-price, currency, intl)
    };
  });

  // Processing Fee
  const processingFeeItem = {
    label: intl.formatMessage({
      id: 'inquiry_booking.inquiry_price_breakdown.processing_fee_label',
      defaultMessage: 'Processing Fee'
    }),
    price: formatPrice(processingFee, currency, intl),
    tooltip: <Tooltip tooltip={TooltipData.PROCESSING_FEE} />
  };

  // Total
  const totalItem = {
    label: intl.formatMessage({
      id: 'inquiry_booking.inquiry_price_breakdown.total_label',
      defaultMessage: 'Total cost'
    }),
    price: formatPrice(total, currency, intl)
  };

  return {
    total: totalItem,
    subtotal: subtotalItem,
    processingFee: processingFeeItem,
    additionalCharges: additionalChargesItems,
    otherDiscounts: otherDiscountsItems,
    dayRateDiscount: dayRateDiscountItem,
    lineItems: [
      subtotalItem,
      dayRateDiscountItem,
      ...additionalChargesItems,
      ...otherDiscountsItems,
      processingFeeItem
    ]
  };
};
